import { useEffect, useRef, useState } from "react";

import type { MediaWidgetProps } from "@fluid-app/react-widgets-types";

import type { CartData } from "../cart-widget/mockData";
import type { MediaItem } from "./types/contexts";
import StandardVideoThumbnail from "../components/media/StandardVideoThumbnail";
import StoryViewerModal from "../components/media/StoryViewerModal";
import ShadowDOMWrapper from "../utils/ShadowDOMWrapper";
import { PlaylistProvider } from "./context/PlaylistContext";
import {
  transformLibraryToMediaItemsWithCart,
  transformMediaToMediaItemWithCart,
} from "./utils/dataTransformers";

const MediaWidget: React.FC<MediaWidgetProps> = ({
  libraryId,
  mediaId,
  height,
  width,
}) => {
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);

  // Fetch media data on mount or when props change
  useEffect(() => {
    const fetchMediaData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Check if window.FluidCommerceSDK is available
        if (typeof window !== "undefined") {
          if (libraryId) {
            // Fetch library/playlist data
            const libraryResponse =
              await window.FluidCommerceSDK.getLibrary(libraryId);
            const {
              mediaItems: transformedItems,
              cartData: transformedCartData,
            } = transformLibraryToMediaItemsWithCart(libraryResponse);
            setMediaItems(transformedItems);
            setCartData(transformedCartData);
          } else if (mediaId) {
            // Fetch single media data
            const mediaResponse =
              await window.FluidCommerceSDK.getMedia(mediaId);
            const {
              mediaItem: transformedItem,
              cartData: transformedCartData,
            } = transformMediaToMediaItemWithCart(mediaResponse);
            setMediaItems([transformedItem]);
            setCartData(transformedCartData);
          } else {
            // No ID provided, use fallback
            throw new Error("No libraryId or mediaId provided");
          }
        } else {
          throw new Error("FluidCommerceSDK not available");
        }
      } catch (err) {
        console.error("Error fetching media data:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch media data",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchMediaData().catch(console.error);
  }, [libraryId, mediaId]);

  const isPlaylist = mediaItems.length > 1;

  // Show loading state
  if (isLoading) {
    return (
      <ShadowDOMWrapper shadowRootRef={shadowRootRef}>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
            <p className="text-gray-600">Loading media...</p>
          </div>
        </div>
      </ShadowDOMWrapper>
    );
  }

  // Show error state
  if (error) {
    return (
      <ShadowDOMWrapper shadowRootRef={shadowRootRef}>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <p className="mb-2 text-red-600">Error loading media</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        </div>
      </ShadowDOMWrapper>
    );
  }

  // Show media content
  return (
    <ShadowDOMWrapper shadowRootRef={shadowRootRef}>
      <PlaylistProvider
        initialMediaItems={mediaItems}
        initialCartData={cartData}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {mediaItems[0] && (
            <StandardVideoThumbnail
              key={mediaItems[0].id}
              type={mediaItems[0].type as "video" | "image" | "website"}
              thumbnail={mediaItems[0].thumbnail}
              videoUrl={mediaItems[0].url}
              onClick={() => setIsStoryModalOpen(true)}
              height={height}
              width={width}
            />
          )}
        </div>

        {/* Modals */}
        <StoryViewerModal
          isOpen={isStoryModalOpen}
          onClose={() => setIsStoryModalOpen(false)}
          isPlaylist={isPlaylist}
          containerRoot={shadowRootRef.current ?? undefined}
        />
      </PlaylistProvider>
    </ShadowDOMWrapper>
  );
};

export default MediaWidget;
